import React, { useRef } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { Clock } from 'lucide-react';
import {
  type AgendaBlock,
  type DayHeader,
  GRID_START_HOUR,
  GRID_END_HOUR,
  HOUR_HEIGHT,
  PX_PER_MINUTE,
  MIN_BLOCK_MINUTES,
  timeToOffset,
  durationToHeight,
  formatTime12,
  timeToMinutes,
  minutesToTime,
  snapMinutes,
} from './constants';

const GRID_HEIGHT = (GRID_END_HOUR - GRID_START_HOUR) * HOUR_HEIGHT;

interface BlockItemProps {
  block: AgendaBlock;
  onClick: (block: AgendaBlock) => void;
  onResize: (id: string, durationMinutes: number) => void;
}

function BlockItem({ block, onClick, onResize }: BlockItemProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: block.id,
    data: { type: 'block', block },
  });

  // True from a resize pointer-down until just after pointer-up, so the
  // synthetic click that follows a resize doesn't open the editor.
  const resizingRef = useRef(false);

  const top = timeToOffset(block.start_time);
  const height = durationToHeight(block.duration_minutes);

  const style: React.CSSProperties = {
    position: 'absolute',
    top,
    height,
    left: 4,
    right: 4,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    backgroundColor: `${block.color}26`,
    borderLeft: `3px solid ${block.color}`,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 1,
  };

  // Resize via local pointer handlers (kept separate from dnd-kit drag).
  const resizeStart = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = true;
    const startY = e.clientY;
    const startDuration = block.duration_minutes;
    const move = (ev: PointerEvent) => {
      const deltaMin = (ev.clientY - startY) / PX_PER_MINUTE;
      const next = Math.max(MIN_BLOCK_MINUTES, snapMinutes(startDuration + deltaMin));
      onResize(block.id, next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      // Reset after the click event (which fires synchronously after
      // pointerup) has been suppressed by the guard in onClick.
      setTimeout(() => {
        resizingRef.current = false;
      }, 0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group rounded-md px-2 py-1 cursor-grab active:cursor-grabbing overflow-hidden select-none shadow-sm"
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDragging && !resizingRef.current) onClick(block);
      }}
    >
      <div className="text-[11px] font-semibold leading-tight" style={{ color: block.color }}>
        {formatTime12(block.start_time)}
      </div>
      <div className="text-xs font-medium text-gray-800 leading-snug truncate">{block.name}</div>
      {block.description && height > 56 && (
        <div className="text-[10px] text-gray-500 leading-snug line-clamp-2 mt-0.5">
          {block.description}
        </div>
      )}
      {/* Resize handle */}
      <div
        onPointerDown={resizeStart}
        className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 flex items-end justify-center"
      >
        <div className="w-8 h-1 mb-0.5 rounded-full" style={{ backgroundColor: block.color }} />
      </div>
    </div>
  );
}

export interface AgendaDayColumnProps {
  header: DayHeader;
  blocks: AgendaBlock[];
  registerDayRef: (dayNumber: number, el: HTMLElement | null) => void;
  onBlockClick: (block: AgendaBlock) => void;
  onBlockResize: (id: string, durationMinutes: number) => void;
  onEmptyClick: (dayNumber: number, startTime: string) => void;
}

export function AgendaDayColumn({
  header,
  blocks,
  registerDayRef,
  onBlockClick,
  onBlockResize,
  onEmptyClick,
}: AgendaDayColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `day:${header.dayNumber}`,
    data: { type: 'day', dayNumber: header.dayNumber },
  });
  const contentRef = useRef<HTMLDivElement | null>(null);

  const setRefs = (el: HTMLDivElement | null) => {
    setNodeRef(el);
    contentRef.current = el;
    registerDayRef(header.dayNumber, el);
  };

  const handleEmptyClick = (e: React.MouseEvent) => {
    if (!contentRef.current) return;
    const rect = contentRef.current.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const minutes = snapMinutes(offsetY / PX_PER_MINUTE) + GRID_START_HOUR * 60;
    onEmptyClick(header.dayNumber, minutesToTime(minutes));
  };

  return (
    <div className="flex-1 min-w-[150px] border-r border-gray-200 last:border-r-0">
      {/* Day header */}
      <div className="h-12 flex flex-col items-center justify-center border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
        <span className="text-sm font-semibold text-gray-800">{header.label}</span>
        {header.sublabel && <span className="text-[11px] text-gray-500">{header.sublabel}</span>}
      </div>

      {/* Grid body */}
      <div
        ref={setRefs}
        onClick={handleEmptyClick}
        className={`relative ${isOver ? 'bg-teal-50/60' : ''}`}
        style={{ height: GRID_HEIGHT }}
      >
        {/* Hour lines */}
        {Array.from({ length: GRID_END_HOUR - GRID_START_HOUR }).map((_, i) => (
          <div
            key={i}
            className="absolute left-0 right-0 border-t border-gray-100"
            style={{ top: i * HOUR_HEIGHT }}
          />
        ))}
        {blocks.map((b) => (
          <BlockItem key={b.id} block={b} onClick={onBlockClick} onResize={onBlockResize} />
        ))}
      </div>
    </div>
  );
}

/** Left-hand time ruler aligned with the day grids. */
export function TimeRuler() {
  return (
    <div className="w-14 flex-shrink-0 border-r border-gray-200">
      <div className="h-12 border-b border-gray-200 bg-gray-50" />
      <div className="relative" style={{ height: GRID_HEIGHT }}>
        {Array.from({ length: GRID_END_HOUR - GRID_START_HOUR }).map((_, i) => {
          const hour = GRID_START_HOUR + i;
          return (
            <div
              key={i}
              className="absolute right-1 -translate-y-1/2 text-[10px] text-gray-400"
              style={{ top: i * HOUR_HEIGHT }}
            >
              {formatTime12(`${String(hour % 24).padStart(2, '0')}:00`)}
            </div>
          );
        })}
        <div className="absolute right-1 text-[10px] text-gray-300 flex items-center gap-0.5" style={{ top: GRID_HEIGHT - 6 }}>
          <Clock className="w-2.5 h-2.5" />
        </div>
      </div>
    </div>
  );
}

export { GRID_HEIGHT, timeToMinutes };
